/**
 * Parses an exam name to extract version numbers for sorting.
 * We need to parse exam names because 4.25 should be after 4.3.
 * @param {string} title - The exam name.
 * @returns {[number, number]} - An array of parsed integers [major, minor].
 */
const parseName = (title) => {
    const match = title.trim().match(/^(\d+)\.(\d+)/);
    if (match) {
        return [parseInt(match[1]), parseInt(match[2])];
    }
    return [0, 0];
};

/**
 * Comparator function for sorting assessments.
 * @param {Object} a - The first assessment object.
 * @param {Object} b - The second assessment object.
 * @returns {number} - The comparison result.
 */
const sortByAssessmentName = (a, b) => {
    const [a1, a2] = parseName(a.examName);
    const [b1, b2] = parseName(b.examName);

    const nameCompare = a1 === b1 ? a2 - b2 : a1 - b1;
    if (nameCompare !== 0) return nameCompare;

    return a.language < b.language ? -1 : (a.language > b.language ? 1 : 0);
};

const sortByYear = (a, b) => {
    return a.year < b.year ? -1 : (a.year > b.year ? 1 : 0);
};

const sortObjectsByCombinedAttributes = (arr, attr1, attr2) => {
  return arr.sort((a, b) => {
    const keyA = `${a[attr1]}-${a[attr2]}`;
    const keyB = `${b[attr1]}-${b[attr2]}`;

    if (keyA < keyB) {
      return -1;
    }
    if (keyA > keyB) {
      return 1;
    }
    return 0;
  });
}

const sortByYearName = (a, b) => {
    const keyA = `${a['year']}-${a['name']}`;
    const keyB = `${b['year']}-${b['name']}`;

    if (keyA < keyB) {
      return -1;
    }
    if (keyA > keyB) {
      return 1;
    }
    return 0;
};

const categoryMap = {
    'energie': 'energy',
    'energy': 'energy',
    'energia': 'energy',
    'energetika': 'energy',
    
    'mss': 'mss',
    'minimum social safeguards': 'mss',
    'minimální sociální záruky': 'mss',
    'criteri sociali minimi di salvaguardia': 'mss',
    'sozialer mindeststandard': 'mss',
    'mindestschutz': 'mss',
    
    'manufacturing': 'manufacturing',
    'verarbeitendes gewerbe/herstellung von waren': 'manufacturing',
    'produzione': 'manufacturing',
    'zpracovatelský průmysl': 'manufacturing',
    
    'construction': 'construction',
    'baugewerbe und immobilien': 'construction',
    'costruzioni': 'construction',
    'stavebnictví a činnosti v oblasti nemovitostí': 'construction',
    
    'professional, scientific and technical activities': 'professional',
    'erbringung von freiberuflichen, wissenschaftlichen und technischen dienstleistungen': 'professional',
    'attività professionali, scientifiche e tecniche': 'professional',
    'odborné, vědecké a technické činnosti': 'professional'
};

function sortNavigationCategories(a, b) {
    const order = [
        'manufacturing',
        'energy',
        'construction',
        'professional',
        'mss',       
    ];
    const normalizedA = categoryMap[a.trim().toLowerCase()] || a.trim().toLowerCase();
    const normalizedB = categoryMap[b.trim().toLowerCase()] || b.trim().toLowerCase();
    const indexA = order.indexOf(normalizedA);
    const indexB = order.indexOf(normalizedB);
    // If both are in the order array, sort them by their defined index
    if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
    }

    // If only A is in the order array, it comes before B
    if (indexA !== -1) return -1;

    // If only B is in the order array, it comes before A
    if (indexB !== -1) return 1;

    // If neither is in the order array, keep their original order (or sort alphabetically as a fallback)
    return normalizedA.localeCompare(normalizedB);
}

export default sortByAssessmentName;
export { sortNavigationCategories, sortByYear, sortObjectsByCombinedAttributes, sortByYearName};
